<template>
  <div class="petmed-attendance-form">
    <span class="pw-label mb-2">Atención en clínica</span>

    <v-alert v-if="clinicsError" type="error" dense text class="mb-2">{{ clinicsError }}</v-alert>

    <v-row class="my-0">
      <v-col cols="6">
        <v-autocomplete
          :value="selectedClinicId"
          :items="clinicItems"
          :loading="loadingClinics"
          label="Clínica Droopy"
          dense
          outlined
          hide-details="auto"
          @change="onClinicChange"
        ></v-autocomplete>
      </v-col>
      <v-col cols="6">
        <v-select
          v-model="selectedVetId"
          :items="doctorItems"
          :loading="loadingDoctors"
          :disabled="!selectedClinicId"
          label="Veterinario que atendió"
          dense
          outlined
          hide-details="auto"
        ></v-select>
        <p v-if="doctorsError" class="petmed-attendance-form__hint error--text">{{ doctorsError }}</p>
        <p v-else-if="doctorsNotice" class="petmed-attendance-form__hint warning--text">{{ doctorsNotice }}</p>
      </v-col>
    </v-row>

    <v-row class="my-0">
      <v-col cols="6">
        <v-select
          :value="selectedServiceId"
          :items="serviceItems"
          :loading="loadingServices"
          :disabled="!selectedClinicId"
          item-text="title"
          item-value="id"
          label="Servicio de la clínica"
          dense
          outlined
          hide-details="auto"
          @change="onServiceChange"
        >
          <template v-slot:item="{ item }">
            <v-list-item-content>
              <v-list-item-title class="text-caption">{{ item.title }}</v-list-item-title>
              <v-list-item-subtitle v-if="serviceSubtitle(item)" class="text-caption">
                {{ serviceSubtitle(item) }}
              </v-list-item-subtitle>
            </v-list-item-content>
          </template>
        </v-select>
        <p v-if="servicesError" class="petmed-attendance-form__hint error--text">{{ servicesError }}</p>
        <p v-else-if="servicesNotice" class="petmed-attendance-form__hint warning--text">{{ servicesNotice }}</p>
      </v-col>
      <v-col cols="6" v-if="hasCategoryOptions">
        <v-select
          v-model="administeredServiceIds"
          :items="selectedService.categoryOptions"
          item-text="title"
          item-value="id"
          label="Vacuna(s) aplicada(s) (opcional)"
          multiple
          dense
          outlined
          hide-details="auto"
        ></v-select>
      </v-col>
    </v-row>

    <v-alert v-if="selectedService && selectedService.isInGracePeriod" type="warning" dense text class="mb-2">
      En carencia ({{ selectedService.graceDaysRemaining }} días): la cobertura Pawer quedará en $0.
    </v-alert>
    <v-alert v-if="exhaustedSelected" type="warning" dense text class="mb-2">
      El tope anual de este servicio ya está usado. Se guardará la cobertura indicada.
    </v-alert>

    <v-row class="my-0">
      <v-col cols="3">
        <v-text-field
          v-model="pawerCoverageAmount"
          label="Cobertura Pawer"
          type="number"
          prefix="$"
          min="0"
          outlined
          dense
          hide-details="auto"
          hide-spin-buttons
        ></v-text-field>
      </v-col>
      <v-col cols="3">
        <v-text-field
          v-model="userPaymentAmount"
          label="Pago del usuario"
          type="number"
          prefix="$"
          min="0"
          outlined
          dense
          hide-details="auto"
          hide-spin-buttons
        ></v-text-field>
      </v-col>
      <v-col cols="3">
        <v-text-field
          v-model="invoiceReference"
          label="N° factura (opcional)"
          outlined
          dense
          hide-details="auto"
        ></v-text-field>
      </v-col>
      <v-col cols="3">
        <v-text-field
          v-model="billReference"
          label="N° boleta (opcional)"
          outlined
          dense
          hide-details="auto"
        ></v-text-field>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import ClinicsService from '../services/clinics.service';

const AMBIGUOUS_COVERAGE_MSG =
  'La configuración de servicios de esta clínica es ambigua para este plan. Contacta al equipo técnico.';

function isValidAmount(value) {
  return value !== null && value !== '' && !isNaN(Number(value)) && Number(value) >= 0;
}

// Sección "Atención en clínica" del registro PetMed (021-petmed-droopy-attendance).
// Encadena clínica → veterinario / servicio cubierto por la membresía y emite:
// - `input`: payload listo para armar el POST de attendances, o null si está incompleto.
// - `validity-change`: boolean.
export default {
  name: 'PetmedAttendanceForm',
  props: {
    // Membresía elegida en el diálogo; se usan id, pet.species.
    membership: { type: Object, required: true },
    // Atención a precargar al rehacerla desde la edición del registro (024): clinicId, vetId,
    // clinicServiceId, administeredServiceIds, montos y referencias.
    initial: { type: Object, default: null },
    // Registro en edición: su propio uso no cuenta en los cupos de los servicios (024 FR-023).
    excludeServiceRecordId: { type: String, default: null },
    // Edición (024 FR-024): un servicio con el tope usado por otros registros se puede elegir, y
    // se prellena con los montos actuales del registro ({ refundedAmount, userSpends }).
    allowExhausted: { type: Boolean, default: false },
    recordAmounts: { type: Object, default: null }
  },
  data() {
    return {
      clinics: [],
      doctors: [],
      services: [],
      selectedClinicId: null,
      selectedVetId: null,
      selectedServiceId: null,
      administeredServiceIds: [],
      pawerCoverageAmount: null,
      userPaymentAmount: null,
      invoiceReference: null,
      billReference: null,
      loadingClinics: false,
      loadingDoctors: false,
      loadingServices: false,
      clinicsError: null,
      doctorsError: null,
      servicesError: null
    };
  },
  computed: {
    clinicItems() {
      return this.clinics
        .filter((c) => c.status === 'active')
        .map((c) => ({ text: `${c.clinicName} — ${c.clinicRut}`, value: c.clinicId }));
    },
    doctorItems() {
      return this.doctors
        .filter((d) => d.status === 'active')
        .map((d) => ({ text: `${d.name} ${d.surname}`, value: d.id }));
    },
    serviceItems() {
      return this.services
        .filter((s) => s.isPawerCovered)
        .map((s) => ({ ...s, disabled: !s.isAvailable && !this.allowExhausted }));
    },
    selectedService() {
      return this.serviceItems.find((s) => s.id === this.selectedServiceId) || null;
    },
    exhaustedSelected() {
      return this.allowExhausted && !!this.selectedService && !this.selectedService.isAvailable;
    },
    hasCategoryOptions() {
      return !!(this.selectedService && this.selectedService.categoryOptions && this.selectedService.categoryOptions.length);
    },
    doctorsNotice() {
      if (!this.selectedClinicId || this.loadingDoctors || this.doctorsError) return null;
      return this.doctorItems.length === 0 ? 'Esta clínica no tiene veterinarios activos registrados.' : null;
    },
    servicesNotice() {
      if (!this.selectedClinicId || this.loadingServices || this.servicesError) return null;
      return this.serviceItems.length === 0
        ? 'Esta clínica no tiene servicios cubiertos por el plan de la membresía.'
        : null;
    },
    isComplete() {
      return (
        !!this.selectedClinicId &&
        !!this.selectedVetId &&
        !!this.selectedService &&
        (this.selectedService.isAvailable || this.allowExhausted) &&
        isValidAmount(this.pawerCoverageAmount) &&
        isValidAmount(this.userPaymentAmount)
      );
    },
    payload() {
      if (!this.isComplete) return null;
      return {
        clinicId: this.selectedClinicId,
        vetId: this.selectedVetId,
        clinicServiceId: this.selectedService.id,
        serviceId: this.selectedService.serviceId,
        administeredServiceIds: this.administeredServiceIds,
        pawerCoverageAmount: Number(this.pawerCoverageAmount),
        userPaymentAmount: Number(this.userPaymentAmount),
        invoiceReference: this.invoiceReference || null,
        billReference: this.billReference || null,
        isInGracePeriod: !!this.selectedService.isInGracePeriod
      };
    }
  },
  watch: {
    payload(value) {
      this.$emit('input', value);
    },
    isComplete: {
      immediate: true,
      handler(value) {
        this.$emit('validity-change', value);
      }
    },
    'membership.id'() {
      this.clearService();
      if (this.selectedClinicId) this.loadServices(this.selectedClinicId);
    }
  },
  created() {
    this.loadClinics();
    if (this.initial) this.applyInitial(this.initial);
  },
  methods: {
    async loadClinics() {
      this.loadingClinics = true;
      this.clinicsError = null;
      const res = await ClinicsService.getDroopyClinics();
      if (res.status === 'ok') this.clinics = res.data || [];
      else this.clinicsError = 'No se pudieron cargar las clínicas Droopy.';
      this.loadingClinics = false;
    },
    async applyInitial(initial) {
      this.selectedClinicId = initial.clinicId || null;
      if (!this.selectedClinicId) return;
      await Promise.all([this.loadDoctors(this.selectedClinicId), this.loadServices(this.selectedClinicId)]);
      this.selectedVetId = initial.vetId || null;
      this.selectedServiceId = initial.clinicServiceId || null;
      this.administeredServiceIds = initial.administeredServiceIds || [];
      this.pawerCoverageAmount = initial.pawerCoverageAmount ?? null;
      this.userPaymentAmount = initial.userPaymentAmount ?? null;
      this.invoiceReference = initial.invoiceReference || null;
      this.billReference = initial.billReference || null;
    },
    onClinicChange(clinicId) {
      this.selectedClinicId = clinicId || null;
      this.selectedVetId = null;
      this.doctors = [];
      this.services = [];
      this.loadingDoctors = false;
      this.loadingServices = false;
      this.clearService();
      if (!this.selectedClinicId) return;
      this.loadDoctors(this.selectedClinicId);
      this.loadServices(this.selectedClinicId);
    },
    // Las respuestas de una clínica que ya no está seleccionada se descartan (cambio rápido
    // de clínica con requests aún en vuelo).
    async loadDoctors(clinicId) {
      this.loadingDoctors = true;
      this.doctorsError = null;
      const res = await ClinicsService.getClinicDoctors(clinicId);
      if (clinicId !== this.selectedClinicId) return;
      if (res.status === 'ok') this.doctors = res.data || [];
      else this.doctorsError = 'No se pudieron cargar los veterinarios de la clínica.';
      this.loadingDoctors = false;
    },
    async loadServices(clinicId) {
      const membershipId = this.membership.id;
      this.loadingServices = true;
      this.servicesError = null;
      const res = await ClinicsService.getClinicServicesForMembership(
        clinicId,
        membershipId,
        this.membership.pet && this.membership.pet.species,
        this.excludeServiceRecordId
      );
      if (clinicId !== this.selectedClinicId || membershipId !== this.membership.id) return;
      if (res.status === 'ok') this.services = res.data || [];
      else if (res.httpCode === 409) this.servicesError = AMBIGUOUS_COVERAGE_MSG;
      else this.servicesError = 'No se pudieron cargar los servicios de la clínica.';
      this.loadingServices = false;
    },
    onServiceChange(clinicServiceId) {
      this.selectedServiceId = clinicServiceId || null;
      this.administeredServiceIds = [];
      const amounts = this.suggestedAmounts(this.selectedService);
      this.pawerCoverageAmount = amounts.pawerCoverageAmount;
      this.userPaymentAmount = amounts.userPaymentAmount;
    },
    // Con el tope usado (y fuera de carencia) lo sugerido por la clínica es $0: en la edición se
    // parte de lo que el registro ya reflejaba (FR-024). En carencia la cobertura sigue en $0.
    suggestedAmounts(service) {
      if (!service) return { pawerCoverageAmount: null, userPaymentAmount: null };
      const record = this.recordAmounts || {};
      const useRecord = this.allowExhausted && !service.isAvailable && !service.isInGracePeriod;
      return {
        pawerCoverageAmount: useRecord ? record.refundedAmount ?? service.pawerServiceCoverage : service.pawerServiceCoverage,
        userPaymentAmount: useRecord ? record.userSpends ?? service.amountToPay : service.amountToPay
      };
    },
    clearService() {
      this.selectedServiceId = null;
      this.administeredServiceIds = [];
      this.pawerCoverageAmount = null;
      this.userPaymentAmount = null;
    },
    serviceSubtitle(item) {
      if (!item.isAvailable) return 'Sin cupos disponibles';
      if (item.isInGracePeriod) return `En carencia (${item.graceDaysRemaining} días)`;
      return null;
    }
  }
};
</script>

<style scoped>
.petmed-attendance-form__hint {
  font-size: 12px;
  margin: 4px 0 0;
}
</style>
